/**
 * Silent over-the-air updates of the web app inside the iOS shell.
 *
 * The deploy publishes every build to the site under /updates/<version>/ plus a manifest at
 * /updates/version.json. When the app is online it compares the manifest with the version it is
 * running; if the site is newer it downloads the files into Capacitor's snapshot folder
 * (Library/NoCloud/ionic_built_snapshots/<version>, the only place Capacitor loads a persisted
 * bundle from), verifies size and SHA-256, and switches to it: immediately when the user is on
 * the home page (nothing to lose), otherwise at the next launch. A bundle that fails to boot is
 * rolled back natively (LiveUpdatePlugin): the new code must call markHealthy() within a few
 * seconds of starting, or the app returns to the built-in bundle. A new App Store binary always
 * starts from its built-in bundle (Capacitor clears the persisted path on a version change).
 */
import { registerPlugin, WebView } from '@capacitor/core'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { isNativeApp } from './native'
import { toBase64 } from '../solo2/bridge'

export const SITE_ORIGIN = 'https://mala-racing.com'
// Capacitor boots a persisted bundle only from Library/NoCloud/ionic_built_snapshots/<name>.
// (Capacitor Filesystem's Directory.Data is Documents on iOS, so the Library directory is used.)
const SNAPSHOT_DIR = Directory.Library
const SNAPSHOTS = 'NoCloud/ionic_built_snapshots'
const COMPLETE_MARKER = '.complete'

/** Version baked in at build time (git commit); see vite.config.ts. */
export const RUNNING_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev'

export interface ManifestFile {
  path: string
  size: number
  sha256: string
}
export interface Manifest {
  version: string
  files: ManifestFile[]
}

interface LiveUpdatePlugin {
  /** Store the path the next launch should load from, without reloading now. */
  persistPath(o: { path: string }): Promise<void>
  /** The running bundle booted fine; cancel the rollback timer. */
  markHealthy(): Promise<void>
  /** Which bundle is running. */
  state(): Promise<{ path: string; persisted: string; builtIn: boolean }>
}
const LiveUpdate = registerPlugin<LiveUpdatePlugin>('LiveUpdate')

export const liveUpdateAvailable = isNativeApp

let checking: Promise<string | null> | null = null

const LAST_KEY = 'mala.liveupdate.last'
export interface LastCheck {
  at: string
  result: 'none' | 'downloaded' | 'switched' | 'error' | 'offline'
  version?: string
  error?: string
}
function remember(c: LastCheck) {
  try {
    localStorage.setItem(LAST_KEY, JSON.stringify(c))
  } catch {
    /* ignore */
  }
}
export function lastCheck(): LastCheck | null {
  try {
    const raw = localStorage.getItem(LAST_KEY)
    return raw ? (JSON.parse(raw) as LastCheck) : null
  } catch {
    return null
  }
}

/** Pure: is the site's bundle different from the running one and worth fetching? */
export function needsUpdate(manifest: Manifest | null, running: string, installed: string[]): 'none' | 'download' | 'switch' {
  if (!manifest || !manifest.version || manifest.version === running || manifest.files.length === 0) return 'none'
  return installed.includes(manifest.version) ? 'switch' : 'download'
}

async function sha256Hex(buf: ArrayBuffer): Promise<string | null> {
  try {
    if (!globalThis.crypto?.subtle) return null
    const d = await crypto.subtle.digest('SHA-256', buf)
    return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return null
  }
}

async function snapshotPath(version: string): Promise<string> {
  const { uri } = await Filesystem.getUri({ directory: SNAPSHOT_DIR, path: `${SNAPSHOTS}/${version}` })
  return decodeURIComponent(uri.replace(/^file:\/\//, '')).replace(/\/+$/, '')
}

async function installedVersions(): Promise<string[]> {
  try {
    const { files } = await Filesystem.readdir({ directory: SNAPSHOT_DIR, path: SNAPSHOTS })
    const out: string[] = []
    for (const f of files) {
      if (f.type !== 'directory') continue
      try {
        await Filesystem.stat({ directory: SNAPSHOT_DIR, path: `${SNAPSHOTS}/${f.name}/${COMPLETE_MARKER}` })
        out.push(f.name)
      } catch {
        /* incomplete download: ignored and cleaned up */
      }
    }
    return out
  } catch {
    return []
  }
}

async function download(manifest: Manifest, onProgress?: (done: number, total: number) => void): Promise<void> {
  const dir = `${SNAPSHOTS}/${manifest.version}`
  await Filesystem.rmdir({ directory: SNAPSHOT_DIR, path: dir, recursive: true }).catch(() => undefined)
  let done = 0
  for (const f of manifest.files) {
    const res = await fetch(`${SITE_ORIGIN}/updates/${manifest.version}/${f.path}`, { cache: 'no-store' })
    if (!res.ok) throw new Error(`${f.path}: HTTP ${res.status}`)
    const buf = await res.arrayBuffer()
    if (buf.byteLength !== f.size) throw new Error(`${f.path}: size ${buf.byteLength}, expected ${f.size}`)
    const hash = await sha256Hex(buf)
    if (hash && hash !== f.sha256) throw new Error(`${f.path}: checksum mismatch`)
    await Filesystem.writeFile({ directory: SNAPSHOT_DIR, path: `${dir}/${f.path}`, data: toBase64(new Uint8Array(buf)), recursive: true })
    onProgress?.(++done, manifest.files.length)
  }
  await Filesystem.writeFile({ directory: SNAPSHOT_DIR, path: `${dir}/${COMPLETE_MARKER}`, data: toBase64(new TextEncoder().encode(manifest.version)), recursive: true })
}

async function pruneSnapshots(keep: string[]): Promise<void> {
  try {
    const { files } = await Filesystem.readdir({ directory: SNAPSHOT_DIR, path: SNAPSHOTS })
    for (const f of files) {
      if (f.type === 'directory' && !keep.includes(f.name)) {
        await Filesystem.rmdir({ directory: SNAPSHOT_DIR, path: `${SNAPSHOTS}/${f.name}`, recursive: true }).catch(() => undefined)
      }
    }
  } catch {
    /* nothing to prune */
  }
}

/**
 * Check the site for a newer bundle and install it. Resolves with the version switched to or
 * staged, or null when nothing changed. Never throws; failures leave the running bundle alone.
 */
export function checkForUpdate(opts: { applyNow: () => boolean } = { applyNow: () => false }): Promise<string | null> {
  if (!liveUpdateAvailable) return Promise.resolve(null)
  if (checking) return checking
  checking = (async () => {
    try {
      let res: Response
      try {
        res = await fetch(`${SITE_ORIGIN}/updates/version.json`, { cache: 'no-store' })
      } catch (e) {
        remember({ at: new Date().toISOString(), result: 'offline', error: e instanceof Error ? e.message : String(e) })
        return null
      }
      if (!res.ok) {
        remember({ at: new Date().toISOString(), result: 'error', error: `manifest HTTP ${res.status}` })
        return null
      }
      const manifest = (await res.json()) as Manifest
      const installed = await installedVersions()
      const action = needsUpdate(manifest, RUNNING_VERSION, installed)
      if (action === 'none') {
        remember({ at: new Date().toISOString(), result: 'none', version: manifest.version })
        return null
      }
      if (action === 'download') await download(manifest)
      const path = await snapshotPath(manifest.version)
      await pruneSnapshots([manifest.version, RUNNING_VERSION])
      if (opts.applyNow()) {
        // Reload into the new bundle now, and keep it for the next launch.
        await LiveUpdate.persistPath({ path })
        remember({ at: new Date().toISOString(), result: 'switched', version: manifest.version })
        await WebView.setServerBasePath({ path })
      } else {
        await LiveUpdate.persistPath({ path })
        remember({ at: new Date().toISOString(), result: 'downloaded', version: manifest.version })
      }
      return manifest.version
    } catch (e) {
      remember({ at: new Date().toISOString(), result: 'error', error: e instanceof Error ? e.message : String(e) })
      console.warn('live update skipped:', e instanceof Error ? e.message : e)
      return null
    } finally {
      checking = null
    }
  })()
  return checking
}

/** Call once at startup: confirms this bundle boots, then looks for a newer one. */
export function startLiveUpdates(applyNow: () => boolean): void {
  if (!liveUpdateAvailable) return
  void LiveUpdate.markHealthy().catch(() => undefined)
  // An earlier build stored snapshots under Documents; Capacitor never loads from there.
  void Filesystem.rmdir({ directory: Directory.Data, path: 'ionic_built_snapshots', recursive: true }).catch(() => undefined)
  // Check shortly after start and whenever the app comes back to the foreground.
  setTimeout(() => void checkForUpdate({ applyNow }), 2500)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForUpdate({ applyNow })
  })
}
